/*
 * Copyright (c) 2025 Pegasystems Inc.
 * All rights reserved.
 */

import DataPageUtils from '../datapage/index';
import type { Bundle, LanguagePack } from '../environment-info/types';
import EnvironmentInfo from '../environment-info';
import Constants from '../constants';

import type { BundleResp } from './types';

/**
 * @description
 * Provides APIs to manage localization bundles.
 */
class LocaleBundlesManager {
  private languagePacks: LanguagePack[];

  constructor() {
    this.languagePacks = [];
  }

  /**
   * This function returns sorted localization bundles based on pyOrder for a given language code.
   * If the specified language is unavailable, it falls back to the default bundle which may contain overrides.
   *
   * @example <caption>LocaleBundlesManager.getLocalizationBundles()</caption>
   * @returns Bundle[]
   *
   * @example <caption>LocaleBundlesManager.getLocalizationBundles('de-DE')</caption>
   * @returns Bundle[]
   */
  getLocalizationBundles(langCode = EnvironmentInfo.getLocale()): Bundle[] {
    const bundles =
      this.languagePacks.find((pack) => pack.pyLangCode === langCode)?.pyBundles ??
      this.languagePacks.find((pack) => pack.pyLangCode === Constants.DEFAULT.toUpperCase())?.pyBundles ??
      [];
    return bundles.sort((a, b) => a.pyOrder - b.pyOrder);
  }

  /**
   * This function loads the bundles for teh given language bundles.
   * If multiple languages are provided, it loads them in parallel.
   *
   * @example <caption>LocaleBundlesManager.loadLocaleBundle(['fr-FR','de-DE'])</caption>
   * @returns Promise<void>
   *
   */
  async loadLocaleBundle(locales: string[]) {
    const langPacks: Promise<unknown>[] = [];
    locales.forEach((locale) => {
      langPacks.push(
        DataPageUtils.getPageDataAsync(
          'D_pzBundlesForLocalization',
          '',
          { locale, onlyApp: 'false' },
          { invalidateCache: true }
        )
      );
    });
    const settled = await Promise.allSettled(langPacks);

    const packs: LanguagePack[] = settled
      .filter((r): r is PromiseFulfilledResult<BundleResp> => r.status === 'fulfilled')
      .flatMap((r) => r.value.pyLanguagePacks);

    this.setLanguagePack(packs);
  }

  /**
   * Sets or updates language packs in the manager.
   * If a language pack already exists, it updates it; otherwise, it adds the new pack.
   *
   * @example
   * const packs: LanguagePack[] = [
   *   { pyLangCode: 'fr-FR', pyBundles: [...] },
   *   { pyLangCode: 'de-DE', pyBundles: [...] }
   * ];
   * LocaleBundlesManager.setLanguagePack(packs);
   */
  setLanguagePack(bundles: LanguagePack[]) {
    bundles.forEach((bundle) => {
      const existingIndex = this.languagePacks.findIndex((pack) => pack.pyLangCode === bundle.pyLangCode);
      if (existingIndex !== -1) {
        this.languagePacks[existingIndex] = bundle;
      } else {
        this.languagePacks.push(bundle);
      }
    });
  }
}

const localeBundlesManager = new LocaleBundlesManager();

export { localeBundlesManager, LocaleBundlesManager };
